import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useFetch, notify } from '../component-items/helpers'
import { globalState } from '../../store'
import { usePermissions } from '../../hooks/usePermissions'
import CardLoading from '../component-items/loading-popover'
import GlobalStyles from '../component-items/styles'
import { Form, Modal, Button } from 'react-bootstrap'
import { UserType } from './RoleBadge'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faTrash, faPlus, faLock, faUsers, faInfoCircle } from '@fortawesome/pro-duotone-svg-icons'

// Types
interface RolePermission {
  segment: string
  access_level: string
}

interface Role {
  id: number
  name: string
  description: string
  user_type: string
  is_system: boolean
  level: number
  user_count: number
  permissions: RolePermission[]
  company?: number | null
}

interface RoleManagementTabProps {
  userType: UserType
  warehouseId?: number
  companyId?: number
  onSegmentHeaderClick?: (segment: string) => void
}

// Styled components for Matrix View
const MatrixContainer = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  border: 1px solid #ebedf2;
  border-radius: 8px;
`

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;

  th,
  td {
    padding: 0.75em 0.5em;
    text-align: center;
    border: 1px solid #ebedf2;
    white-space: nowrap;
  }

  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #48465b;
    font-size: 0.85em;
  }

  td:first-child {
    text-align: left;
    background-color: #f8f9fa;
    min-width: 200px;
  }

  tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  tbody tr:hover td:first-child {
    background-color: #f0f1f3;
  }
`

const SegmentHeaderButton = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #2f6ae5;
  }
`

const RoleCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
`

const RoleName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #212529;
`

const RoleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.8em;
  color: #6c757d;
`

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const SystemBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.1em 0.35em;
  border-radius: 3px;
  font-size: 0.7em;
  background: #e9ecef;
  color: #6c757d;
  font-weight: 500;
`

const LevelBadge = styled.span<{ $level: string }>`
  display: inline-block;
  padding: 0.3em 0.6em;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.8em;
  min-width: 55px;
  text-align: center;
  background-color: ${({ $level }) => {
    switch ($level) {
      case 'admin':
        return '#f3e5f5'
      case 'operator':
        return '#e8f5e9'
      case 'viewer':
        return '#e3f2fd'
      case 'none':
      default:
        return '#f5f5f5'
    }
  }};
  color: ${({ $level }) => {
    switch ($level) {
      case 'admin':
        return '#7b1fa2'
      case 'operator':
        return '#2e7d32'
      case 'viewer':
        return '#1565c0'
      case 'none':
      default:
        return '#9e9e9e'
    }
  }};
`

const ActionCell = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.35rem;
`

const ActionButton = styled.button<{ $variant?: 'danger' | 'primary' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.5rem;
  border: 1px solid ${(props) => (props.$variant === 'danger' ? '#dc3545' : '#0d6efd')};
  background: transparent;
  color: ${(props) => (props.$variant === 'danger' ? '#dc3545' : '#0d6efd')};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8em;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.$variant === 'danger' ? '#dc3545' : '#0d6efd')};
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background: transparent;
      color: ${(props) => (props.$variant === 'danger' ? '#dc3545' : '#0d6efd')};
    }
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #6c757d;
  text-align: center;

  svg {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  h4 {
    margin-bottom: 0.5rem;
    color: #495057;
  }

  p {
    margin: 0;
    font-size: 0.9em;
  }
`

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 0.85em;
  color: #6c757d;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`

// Access level display names
const ACCESS_LEVEL_LABELS: Record<string, string> = {
  admin: 'Admin',
  operator: 'Operator',
  viewer: 'Viewer',
  none: 'None',
}

// Segment display names (matching backend BusinessSegment enum)
const SEGMENT_LABELS: Record<string, string> = {
  outbound: 'Outbound',
  inbound: 'Inbound',
  inventory: 'Inventory',
  products: 'Products',
  vas: 'VAS',
  billing: 'Billing',
  postage: 'Postage',
  integrations: 'Integrations',
  settings: 'Settings',
  reports: 'Reports',
}

// All segments in display order (matching backend BusinessSegment enum)
const ALL_SEGMENTS = [
  'outbound',
  'inbound',
  'inventory',
  'products',
  'vas',
  'billing',
  'postage',
  'integrations',
  'settings',
  'reports',
]

export const RoleManagementTab = ({
  userType,
  warehouseId,
  companyId,
  onSegmentHeaderClick,
}: RoleManagementTabProps) => {
  const {
    state: { csrf },
  } = globalState()
  const { hasAccess, hasGlobalAccess } = usePermissions()

  // Viewing roles requires settings:operator (scoped is fine)
  // But creating/editing/deleting role definitions requires GLOBAL settings:admin per spec
  const canManageRoles = hasGlobalAccess('settings', 'admin')

  const [refreshKey, setRefreshKey] = useState(0)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingRole, setEditingRole] = useState<Role | null>(null)

  // Build API URL based on user type
  const rolesUrl =
    userType === 'warehouse'
      ? `/api/rbac/roles/?user_type=warehouse&refresh=${refreshKey}`
      : `/api/rbac/roles/?user_type=merchant&refresh=${refreshKey}`

  const { response, loaded, error, placeholder }: any = useFetch(rolesUrl, {})
  const roles: Role[] = loaded ? [...(response?.results || response || [])].sort((a, b) => b.level - a.level) : []

  const refresh = () => setRefreshKey((k) => k + 1)

  const handleCreateRole = () => {
    setEditingRole(null)
    setShowCreateModal(true)
  }

  const handleEditRole = (role: Role) => {
    setEditingRole(role)
    setShowCreateModal(true)
  }

  const handleDeleteRole = async (role: Role) => {
    if (role.user_count > 0) {
      notify({
        type: 'error',
        message: `Cannot delete role "${role.name}" - it is assigned to ${role.user_count} user(s). Remove all assignments first.`,
      })
      return
    }

    notify({
      type: 'warning',
      message: `Are you sure you want to delete the role "${role.name}"?`,
      requestConfirmation: true,
      onSubmit: async () => {
        const resp = await fetch(`/api/rbac/roles/${role.id}/`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrf,
          },
        })
        if (resp.ok) {
          notify({ type: 'success', message: 'Role deleted successfully' })
          refresh()
        } else {
          const data = await resp.json().catch(() => ({}))
          notify({ type: 'error', message: data?.error?.message || data?.error || 'Failed to delete role' })
        }
      },
      autoClose: false,
    })
  }

  const handleModalClose = () => {
    setShowCreateModal(false)
    setEditingRole(null)
  }

  const handleRoleSaved = () => {
    handleModalClose()
    refresh()
  }

  // Get permission level for a role and segment
  const getPermissionLevel = (role: Role, segment: string): string => {
    const perm = role.permissions?.find((p) => p.segment === segment)
    return perm?.access_level || 'none'
  }

  if (!loaded) {
    return <CardLoading text={placeholder} error={error} />
  }

  return (
    <div style={{ padding: '0 1em' }}>
      {/* Header with Add button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9em' }}>
            Manage roles and their permissions. System roles cannot be modified.
          </p>
        </div>
        {canManageRoles && (
          <GlobalStyles.Button onClick={handleCreateRole}>
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '0.5em' }} />
            Create Role
          </GlobalStyles.Button>
        )}
      </div>

      {/* Roles Matrix */}
      {roles.length === 0 ? (
        <EmptyState>
          <FontAwesomeIcon icon={faInfoCircle} />
          <h4>No roles found</h4>
          <p>Create a custom role to get started.</p>
        </EmptyState>
      ) : (
        <>
          <MatrixContainer>
            <MatrixTable>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', minWidth: '200px' }}>Role</th>
                  {ALL_SEGMENTS.map((segment) => (
                    <th key={segment}>
                      {onSegmentHeaderClick ? (
                        <SegmentHeaderButton onClick={() => onSegmentHeaderClick(segment)}>
                          {SEGMENT_LABELS[segment]}
                        </SegmentHeaderButton>
                      ) : (
                        SEGMENT_LABELS[segment]
                      )}
                    </th>
                  ))}
                  <th style={{ minWidth: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role.id}>
                    <td>
                      <RoleCell>
                        <RoleName>
                          {role.name}
                          {role.is_system && (
                            <SystemBadge>
                              <FontAwesomeIcon icon={faLock} />
                              System
                            </SystemBadge>
                          )}
                        </RoleName>
                        <RoleMeta>
                          <MetaItem>
                            <FontAwesomeIcon icon={faUsers} />
                            {role.user_count}
                          </MetaItem>
                          <MetaItem>Level {role.level}</MetaItem>
                        </RoleMeta>
                      </RoleCell>
                    </td>
                    {ALL_SEGMENTS.map((segment) => {
                      const level = getPermissionLevel(role, segment)
                      return (
                        <td key={segment}>
                          <LevelBadge $level={level}>{ACCESS_LEVEL_LABELS[level]}</LevelBadge>
                        </td>
                      )
                    })}
                    <td>
                      {role.is_system ? (
                        <span style={{ color: '#adb5bd', fontSize: '0.8em' }}>Protected</span>
                      ) : canManageRoles ? (
                        <ActionCell>
                          <ActionButton onClick={() => handleEditRole(role)} title="Edit role">
                            <FontAwesomeIcon icon={faEdit} />
                          </ActionButton>
                          <ActionButton
                            $variant="danger"
                            onClick={() => handleDeleteRole(role)}
                            disabled={role.user_count > 0}
                            title={role.user_count > 0 ? 'Cannot delete - role is in use' : 'Delete role'}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </ActionButton>
                        </ActionCell>
                      ) : (
                        <span style={{ color: '#adb5bd', fontSize: '0.8em' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </MatrixTable>
          </MatrixContainer>

          <Legend>
            <LegendItem>
              <LevelBadge $level="admin">Admin</LevelBadge>
              <span>Full access</span>
            </LegendItem>
            <LegendItem>
              <LevelBadge $level="operator">Operator</LevelBadge>
              <span>Create & modify</span>
            </LegendItem>
            <LegendItem>
              <LevelBadge $level="viewer">Viewer</LevelBadge>
              <span>Read only</span>
            </LegendItem>
            <LegendItem>
              <LevelBadge $level="none">None</LevelBadge>
              <span>No access</span>
            </LegendItem>
            <LegendItem style={{ marginLeft: 'auto' }}>
              <SystemBadge>
                <FontAwesomeIcon icon={faLock} />
                System
              </SystemBadge>
              <span>Cannot be modified</span>
            </LegendItem>
          </Legend>
        </>
      )}

      {/* Create/Edit Modal */}
      <RoleEditModal
        show={showCreateModal}
        onHide={handleModalClose}
        onSaved={handleRoleSaved}
        role={editingRole}
        userType={userType}
        companyId={companyId}
        csrf={csrf}
      />
    </div>
  )
}

// Role Edit Modal Component
interface RoleEditModalProps {
  show: boolean
  onHide: () => void
  onSaved: () => void
  role: Role | null
  userType: UserType
  companyId?: number
  csrf: string
}

const PermissionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
`

const SegmentLabel = styled.span`
  font-weight: 500;
`

// Segments and access levels for the edit modal (matching backend)
const SEGMENTS = [
  'outbound',
  'inbound',
  'inventory',
  'products',
  'vas',
  'billing',
  'postage',
  'integrations',
  'settings',
  'reports',
]
const ACCESS_LEVELS = ['none', 'viewer', 'operator', 'admin']

const RoleEditModal = ({ show, onHide, onSaved, role, userType, companyId, csrf }: RoleEditModalProps) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [level, setLevel] = useState(50)
  const [permissions, setPermissions] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (role) {
      setName(role.name)
      setDescription(role.description || '')
      setLevel(role.level)
      const permMap: Record<string, string> = {}
      role.permissions.forEach((p) => {
        permMap[p.segment] = p.access_level
      })
      setPermissions(permMap)
    } else {
      setName('')
      setDescription('')
      setLevel(50)
      setPermissions({})
    }
  }, [role, show])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)

    const permArray = Object.entries(permissions)
      .filter(([_, level]) => level && level !== 'none')
      .map(([segment, access_level]) => ({ segment, access_level }))

    const payload: any = {
      name,
      description,
      level,
      user_type: userType,
      permissions: permArray,
    }

    if (userType === 'merchant' && companyId) {
      payload.company = companyId
    }

    try {
      const url = role ? `/api/rbac/roles/${role.id}/` : '/api/rbac/roles/'
      const method = role ? 'PATCH' : 'POST'

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrf,
        },
        body: JSON.stringify(payload),
      })

      if (resp.ok) {
        notify({ type: 'success', message: role ? 'Role updated successfully' : 'Role created successfully' })
        onSaved()
      } else {
        const data = await resp.json().catch(() => ({}))
        notify({ type: 'error', message: data?.error?.message || data?.error || 'Failed to save role' })
      }
    } finally {
      setSaving(false)
    }
  }

  const handlePermissionChange = (segment: string, level: string) => {
    setPermissions((prev) => ({ ...prev, [segment]: level }))
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{role ? 'Edit Role' : 'Create Role'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Role Name *</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Inventory Manager"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description of this role's purpose"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Role Level</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={99}
              value={level}
              onChange={(e) => setLevel(Number(e.target.value))}
            />
            <Form.Text className="text-muted">
              Higher levels have more authority. Users can only assign roles at or below their own level.
            </Form.Text>
          </Form.Group>

          <div className="mb-3">
            <Form.Label>Permissions</Form.Label>
            <div style={{ border: '1px solid #dee2e6', borderRadius: '8px' }}>
              {SEGMENTS.map((segment) => (
                <PermissionRow key={segment}>
                  <SegmentLabel>{SEGMENT_LABELS[segment] || segment}</SegmentLabel>
                  <Form.Select
                    style={{ width: '150px' }}
                    value={permissions[segment] || 'none'}
                    onChange={(e) => handlePermissionChange(segment, e.target.value)}
                  >
                    {ACCESS_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {ACCESS_LEVEL_LABELS[level] || level}
                      </option>
                    ))}
                  </Form.Select>
                </PermissionRow>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving || !name.trim()}>
            {saving ? 'Saving...' : role ? 'Update Role' : 'Create Role'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
